import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

export interface FilterableItem {
  name: string;
  category?: string | null;
  group?: string | null;
}

interface ItemNameFilterProps {
  items: FilterableItem[];
  selected: string[];
  onChange: (names: string[]) => void;
}

interface GroupNode {
  group: string;
  names: string[];
}

interface CategoryNode {
  category: string;
  groups: GroupNode[];
  names: string[];
}

function buildTree(items: FilterableItem[]): CategoryNode[] {
  const byCategory = new Map<string, Map<string, Set<string>>>();
  items.forEach((i) => {
    const category = i.category || 'Uncategorized';
    const group = i.group || 'Ungrouped';
    if (!byCategory.has(category)) byCategory.set(category, new Map());
    const groups = byCategory.get(category)!;
    if (!groups.has(group)) groups.set(group, new Set());
    groups.get(group)!.add(i.name);
  });
  return Array.from(byCategory.entries())
    .map(([category, groups]) => {
      const groupNodes = Array.from(groups.entries())
        .map(([group, names]) => ({ group, names: Array.from(names).sort() }))
        .sort((a, b) => a.group.localeCompare(b.group));
      const names = groupNodes.flatMap((g) => g.names);
      return { category, groups: groupNodes, names };
    })
    .sort((a, b) => a.category.localeCompare(b.category));
}

/**
 * Category -> Group -> Item checkbox tree, collapsed by default so it stays
 * usable with thousands of items. Checking a category/group toggles every
 * item under it; search auto-expands the branches that match.
 */
export function ItemNameFilter({ items, selected, onChange }: ItemNameFilterProps) {
  const [search, setSearch] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const tree = useMemo(() => buildTree(items), [items]);
  const allNames = useMemo(() => Array.from(new Set(items.map((i) => i.name))), [items]);
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const term = search.trim().toLowerCase();

  const visibleTree = useMemo(() => {
    if (!term) return tree;
    return tree
      .map((cat) => {
        const groups = cat.groups
          .map((g) => ({ ...g, names: g.names.filter((n) => n.toLowerCase().includes(term)) }))
          .filter((g) => g.names.length > 0);
        return { ...cat, groups, names: groups.flatMap((g) => g.names) };
      })
      .filter((cat) => cat.names.length > 0);
  }, [tree, term]);

  const isCategoryExpanded = (category: string) => !!term || expandedCategories.has(category);
  const isGroupExpanded = (key: string) => !!term || expandedGroups.has(key);

  const toggleCategory = (category: string) => setExpandedCategories((prev) => {
    const next = new Set(prev);
    next.has(category) ? next.delete(category) : next.add(category);
    return next;
  });
  const toggleGroup = (key: string) => setExpandedGroups((prev) => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  const setNamesSelected = (names: string[], select: boolean) => {
    if (select) {
      const merged = new Set(selected);
      names.forEach((n) => merged.add(n));
      onChange(Array.from(merged));
    } else {
      const excluded = new Set(names);
      onChange(selected.filter((n) => !excluded.has(n)));
    }
  };
  const toggleOne = (name: string) => {
    onChange(selectedSet.has(name) ? selected.filter((n) => n !== name) : [...selected, name]);
  };

  const selectionState = (names: string[]): 'all' | 'some' | 'none' => {
    const count = names.filter((n) => selectedSet.has(n)).length;
    if (count === 0) return 'none';
    return count === names.length ? 'all' : 'some';
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-lg font-semibold">Filter Items</h3>
        <div className="flex items-center gap-3 text-sm">
          <span className="text-gray-500">
            {selected.length === 0 ? 'Showing all items' : `${selected.length} of ${allNames.length} selected`}
          </span>
          <button type="button" className="text-primary-600 hover:underline" onClick={() => onChange(allNames)}>
            Select all
          </button>
          <button type="button" className="text-primary-600 hover:underline" onClick={() => onChange([])}>
            Clear
          </button>
        </div>
      </div>
      <input
        type="text"
        placeholder="Search items..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="input w-full mb-2"
      />
      <div className="max-h-80 overflow-y-auto border rounded divide-y divide-gray-100">
        {visibleTree.length === 0 ? (
          <div className="p-2 text-sm text-gray-400">No items found</div>
        ) : (
          visibleTree.map((cat) => {
            const catState = selectionState(cat.names);
            const catExpanded = isCategoryExpanded(cat.category);
            return (
              <div key={cat.category}>
                <div className="flex items-center gap-2 px-2 py-1.5 bg-gray-50">
                  <button type="button" onClick={() => toggleCategory(cat.category)} className="text-gray-500" aria-label="Toggle category">
                    {catExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </button>
                  <input
                    type="checkbox"
                    checked={catState === 'all'}
                    ref={(el) => { if (el) el.indeterminate = catState === 'some'; }}
                    onChange={() => setNamesSelected(cat.names, catState !== 'all')}
                    className="rounded border-gray-300"
                  />
                  <span className="font-medium text-sm flex-1">{cat.category}</span>
                  <span className="text-xs text-gray-400">{cat.names.length}</span>
                </div>
                {catExpanded && cat.groups.map((g) => {
                  const key = `${cat.category}::${g.group}`;
                  const grpState = selectionState(g.names);
                  const grpExpanded = isGroupExpanded(key);
                  return (
                    <div key={key} className="pl-4">
                      <div className="flex items-center gap-2 px-2 py-1">
                        <button type="button" onClick={() => toggleGroup(key)} className="text-gray-400" aria-label="Toggle group">
                          {grpExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                        </button>
                        <input
                          type="checkbox"
                          checked={grpState === 'all'}
                          ref={(el) => { if (el) el.indeterminate = grpState === 'some'; }}
                          onChange={() => setNamesSelected(g.names, grpState !== 'all')}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm text-gray-700 flex-1">{g.group}</span>
                        <span className="text-xs text-gray-400">{g.names.length}</span>
                      </div>
                      {grpExpanded && (
                        <div className="pl-6 grid grid-cols-2 md:grid-cols-3 gap-1 pb-1">
                          {g.names.map((name) => (
                            <label key={name} className="flex items-center gap-2 text-sm truncate">
                              <input
                                type="checkbox"
                                checked={selectedSet.has(name)}
                                onChange={() => toggleOne(name)}
                                className="rounded border-gray-300"
                              />
                              <span className="truncate" title={name}>{name}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
